import { CommonModule } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, effect, input, output, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { BaseComponent, Grid, ListItem } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import {
    WorkOrderHistoryEquipment,
    WorkOrderHistoryItem,
    WorkOrderHistoryOpenEvent,
    WorkOrderHistoryResponse,
} from './work-order-history.model'

interface WorkOrderHistoryRow extends WorkOrderHistoryItem {
    rowKey: string
    dateCreatedValue?: Date
    dateCompletedValue?: Date
}

@Component({
    selector: 'app-work-order-history',
    templateUrl: './work-order-history.component.html',
    styleUrls: ['./work-order-history.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule, WjInputModule, WjGridModule, WjGridFilterModule, ActionToolbarCount, ActionToolbarSearchComponent],
    host: {
        '[class.fill-height]': 'fillHeight()',
    },
})
export class WorkOrderHistoryComponent extends BaseComponent {
    equipment = input('')
    showSummary = input(true)
    fillHeight = input(false)
    selectedWorkOrder = input('')
    selectedWebAppsId = input<string | undefined>(undefined)
    openWorkOrder = output<WorkOrderHistoryOpenEvent>()

    years = signal(3)
    yearsOptions: ListItem<number>[] = [
        new ListItem(1, '1 year'),
        new ListItem(3, '3 years'),
        new ListItem(5, '5 years'),
        new ListItem(0, 'All'),
    ]

    grid: Grid<WorkOrderHistoryRow>
    summary: WorkOrderHistoryEquipment | null = null
    isLoading = false
    private applyingSelection = false

    constructor() {
        super()
        this.grid = new Grid<WorkOrderHistoryRow>(this.services, 'rowKey', this)
            .wrapColumnHeaders()
            .isReadOnly(true)
            .doInitialSelection(false)
            .filterBy(item => [item.workOrderNumber, item.description, item.mechanicName, item.status, item.source])

        this.grid.selectionChanged.on(item => this.onGridSelection(item))

        effect(() => {
            this.loadHistory(this.equipment(), this.years())
        })

        effect(() => {
            this.selectedWorkOrder()
            this.selectedWebAppsId()
            this.applySelectedWorkOrder()
        })
    }

    loadHistory(equipment: string, years: number) {
        const code = (equipment ?? '').trim()
        if (!code) {
            this.summary = null
            this.grid.items = []
            this.isLoading = false
            this.initialLoadingComplete()
            this.refreshParentLayout()
            return
        }

        this.isLoading = true
        const params = new HttpParams().set('equipment', code).set('years', String(years))
        this.cancelableRequest(
            'history',
            true,
            this.services.data.get<WorkOrderHistoryResponse>('api/EquipmentWorkOrder/history', params).subscribe({
                next: response => {
                    this.summary = response.equipment ?? null
                    this.grid.items = (response.items ?? []).map(item => this.toRow(item))
                    this.isLoading = false
                    this.initialLoadingComplete()
                    this.applySelectedWorkOrder()
                    setTimeout(() => this.applySelectedWorkOrder(), 0)
                    this.refreshParentLayout()
                },
                error: () => {
                    this.summary = null
                    this.grid.items = []
                    this.isLoading = false
                    this.initialLoadingComplete()
                    this.refreshParentLayout()
                },
            }),
        )
    }

    onYearsChanged(value: number | string) {
        this.years.set(Number(value) || 0)
    }

    private refreshParentLayout() {
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 0)
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 100)
    }

    onOpen(row: WorkOrderHistoryRow) {
        this.openWorkOrder.emit({
            source: row.source,
            webAppsId: row.webAppsId,
            workOrderNumber: row.workOrderNumber,
            equipment: row.equipment,
            job: row.job,
        })
    }

    private onGridSelection(row: WorkOrderHistoryRow | null) {
        if (this.applyingSelection || !row) return
        this.onOpen(row)
    }

    private applySelectedWorkOrder() {
        const workOrder = (this.selectedWorkOrder() ?? '').trim()
        const webAppsId = this.selectedWebAppsId() ?? ''
        if (!workOrder && !webAppsId) return

        const match =
            this.grid.items.find(item => item.workOrderNumber === workOrder && (item.webAppsId ?? '') === webAppsId) ??
            this.grid.items.find(item => item.workOrderNumber === workOrder)
        if (!match || this.grid.selectedItem === match) return

        this.applyingSelection = true
        this.grid.selectedItem = match
        this.applyingSelection = false
    }

    private toRow(item: WorkOrderHistoryItem): WorkOrderHistoryRow {
        return {
            ...item,
            rowKey: `${item.source}|${item.webAppsId ?? item.workOrderNumber}`,
            dateCreatedValue: item.dateCreated ? new Date(item.dateCreated) : undefined,
            dateCompletedValue: item.dateCompleted ? new Date(item.dateCompleted) : undefined,
        }
    }
}
